import React, { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";

import { useAppDispatch } from "hooks/redux";
import { capitalize, unsavedChangesExist } from "helpers/Helpers";

import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import Card from "components/card/Card";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import "./facilityFormEdit.scss";
import { Dictionary, cloneDeep, groupBy } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FormEditContext, FormEditHandleUpdateInput } from "./FormEditContext";
import { ButtonNew as Button } from "components/buttonNew";
import FormEditInputLayout from "./dragAndDropElements/FormEditInputLayout";
import { ISelectedEditInput } from "components/draggableEditInput/DraggableEditInput";
import useUnsavedChangesContainer from "hooks/useUnsavedChangesContainer/useUnsavedChangesContainer";
import { generateInputRowsWithSequentialPositions } from "hooks/editInputDragAndDrop/useDropEditInputRow/useDropEditInputRow";
import Checkbox from "components/form/checkbox/Checkbox";

export type FormInputRows = Dictionary<FormInput[]>;

import { useTranslation } from "react-i18next";
import {
  FormInput,
  FormInputAssignedAttribute,
  FormInputTypes,
  isFormInputTypeAssignedAttribute,
} from "redux/reducers/models/formInput";

import {
  DeleteFormInput,
  GetFormInput,
  PostFormInput,
  PostFormInputParams,
  PutFormInput,
  PutFormInputParams,
  PutFormInputPosition,
  PutFormInputPositions,
} from "api/rpc/2024-04/facilityAdmin/facility/form/formInput";
import {
  GetFacilityForm,
  PutFacilityForm,
  PutFacilityFormParams,
} from "api/rpc/2024-04/facilityAdmin/facility/form/form";
import Callout from "components/callout/Callout";

export interface INewInputModal extends Omit<PostFormInputParams, "type"> {
  open: boolean;
  type: FormInputTypes | "none";
}

interface IEditFormModal {
  open: boolean;
  formTitle: string;
}

interface IProcessForm {
  previousCheck: boolean;
  updatedCheck: boolean;
}

export default function FacilityFormEdit() {
  const { id } = useParams<{ id: string }>();
  const { Option } = Select;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [formTitle, setFormTitle] = useState<string>("");
  const [hideAddInput, setHideAddInput] = useState<boolean>(false);

  const [formProcess, setFormProcess] = useState<IProcessForm>({
    previousCheck: false,
    updatedCheck: false,
  });

  const [formInputRowsContainer, formInputRowsContainerActions] = useUnsavedChangesContainer<FormInputRows>();

  const [newInputModal, setNewInputModal] = useState<INewInputModal>({
    form_id: Number(id),
    open: false,
    label: "",
    help_text: "",
    required: true,
    type: "none",
  });

  const [editFormModal, setEditFormModal] = useState<IEditFormModal>({
    open: false,
    formTitle: "",
  });

  const [selectedFormInput, setSelectedFormInput] = useState<ISelectedEditInput>({
    id: null,
    deletePopupOpen: false,
    editModalOpen: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadForm(source.token);
    void loadFormInputs(source.token);

    return () => {
      source.cancel();
    };
  }, [id]);

  /** GET and set form data */
  async function loadForm(token?: CancelToken) {
    const res = await GetFacilityForm({ id: Number(id) }, token ? false : true, token);

    if (res.status !== StatusCode.OK) {
      return;
    }

    setFormTitle(res.data[0].title);
    setFormProcess({
      updatedCheck: !!res.data[0].process_product_with_response,
      previousCheck: !!res.data[0].process_product_with_response,
    });
  }

  /** GET and set form inputs */
  async function loadFormInputs(token?: CancelToken) {
    const res = await GetFormInput({ form_id: Number(id) }, false, token);

    if (res.status !== StatusCode.OK) {
      formInputRowsContainerActions.set(undefined);
      return;
    }

    const formInputs = res.data;

    if (formInputs.length > 0 || res.data.length > 0) {
      setHideAddInput(true);
    }
    formInputs.sort((prev, next) => prev.row - next.row);

    const formInputRows: FormInputRows = groupBy(formInputs, formInput => formInput.row);

    for (const row in formInputRows) {
      formInputRows[row].sort((prev, next) => prev.column - next.column);
    }

    formInputRowsContainerActions.set(formInputRows);
  }

  async function handleAddNewInput() {
    let updatedFormInputRows: FormInputRows = undefined;

    if (formInputRowsContainer.changesExist) {
      updatedFormInputRows = await updateFormInputRowPositions();

      if (!updatedFormInputRows) {
        return;
      }
    }

    updatedFormInputRows ??= cloneDeep(formInputRowsContainer.updatedState);

    // Destructure and remove open prop
    const deepClone: INewInputModal = JSON.parse(JSON.stringify(newInputModal));
    const params = (({ ...rest }) => rest)(deepClone);

    const res = await PostFormInput(
      {
        form_id: Number(id),
        ...params,
        type: params.type === "none" ? null : params.type,
        required: params.type === "text" ? false : params.required,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating input.")));
      return;
    }

    dispatch(showSuccess(t("Successfully added a new input.")));
    closeNewInputModal();

    const newFormInput = res.data;
    const row = String(newFormInput.row);

    const updatedFormInputRowsWithInput: FormInputRows = {
      ...updatedFormInputRows,
      [row]: [newFormInput],
    };

    formInputRowsContainerActions.set(updatedFormInputRowsWithInput);
  }

  /** DELETE a form input. Update state. */
  async function handleRemoveFormInput(id: number) {
    let updatedFormInputRows: FormInputRows = undefined;

    if (formInputRowsContainer.changesExist) {
      updatedFormInputRows = await updateFormInputRowPositions();

      if (!updatedFormInputRows) {
        return;
      }
    }

    updatedFormInputRows ??= cloneDeep(formInputRowsContainer.updatedState);

    const res = await DeleteFormInput({ id: id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("There was an error removing the input.")));
      return;
    }

    dispatch(showSuccess(t("Successfully removed the input.")));

    setSelectedFormInput(prev => ({ ...prev, id: null, deletePopupOpen: false }));

    let rowDeletingFrom = "";

    Object.keys(updatedFormInputRows).forEach(row => {
      if (updatedFormInputRows[row]?.some(formInput => formInput.id === id)) {
        rowDeletingFrom = row;
      }
    });

    if (rowDeletingFrom === "") {
      return;
    }

    const { [rowDeletingFrom]: originalRow, ...rest } = updatedFormInputRows;

    const updatedRow = originalRow
      .filter(formInput => formInput.id !== id)
      .map((formInput, index) => ({ ...formInput, column: index + 1 }));

    const updatedFormInputRowsWithDelete: FormInputRows = {
      ...rest,
      ...(updatedRow.length > 0 ? { [rowDeletingFrom]: updatedRow } : {}),
    };

    if (updatedRow.length > 0) {
      formInputRowsContainerActions.set(updatedFormInputRowsWithDelete);
    } else {
      const updatedFormInputRowsWithShift: FormInputRows = {};
      const numericRowDeletingFrom = Number(rowDeletingFrom);

      Object.keys(updatedFormInputRowsWithDelete).forEach(row => {
        const numericRow = Number(row);

        if (numericRow > numericRowDeletingFrom) {
          const shiftedRow = numericRow - 1;

          updatedFormInputRowsWithShift[String(shiftedRow)] = updatedFormInputRowsWithDelete[row].map(formInput => {
            return {
              ...formInput,
              row: shiftedRow,
            };
          });
        } else {
          updatedFormInputRowsWithShift[row] = updatedFormInputRowsWithDelete[row];
        }
      });

      formInputRowsContainerActions.set(updatedFormInputRowsWithShift);
    }
  }

  /** PUT a form input.  Update state. */
  const handleUpdateInput: FormEditHandleUpdateInput = async (
    type,
    id,
    label,
    helpText,
    value,
    assignedAttribute,
    isRequired,
  ) => {
    let updatedFormInputRows: FormInputRows = undefined;

    if (formInputRowsContainer.changesExist) {
      updatedFormInputRows = await updateFormInputRowPositions();

      if (!updatedFormInputRows) {
        return;
      }
    }

    updatedFormInputRows ??= formInputRowsContainer.updatedState;

    let updatedAssignedAttribute: FormInputAssignedAttribute = null;

    if (type === "input" && isFormInputTypeAssignedAttribute(assignedAttribute)) {
      updatedAssignedAttribute = assignedAttribute;
    }

    const params: PutFormInputParams = {
      input_id: id,
      label: label,
      help_text: helpText,
      values: value,
      assigned_attribute: updatedAssignedAttribute,
      required: isRequired,
    };

    const res = await PutFormInput(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating input.")));
      return;
    }

    dispatch(showSuccess(t("Input successfully saved.")));

    const updatedFormInput = cloneDeep(res.data);
    const updatedFormInputRowsWithUpdateInput: FormInputRows = cloneDeep(updatedFormInputRows);

    for (const row in updatedFormInputRowsWithUpdateInput) {
      const updatedFormInputRow = updatedFormInputRowsWithUpdateInput[row];

      for (let i = 0; i < updatedFormInputRow.length; i++) {
        if (updatedFormInputRow[i].id === updatedFormInput.id) {
          updatedFormInputRow[i] = updatedFormInput;
        }
      }
    }

    formInputRowsContainerActions.set(updatedFormInputRowsWithUpdateInput);
    closeFormInputEditModal();
  };

  function openEditFormModal() {
    setEditFormModal(prev => ({
      ...prev,
      open: true,
      formTitle: formTitle,
    }));
  }

  function closeEditFormModal() {
    setEditFormModal(prev => ({
      ...prev,
      open: false,
      formTitle: "",
    }));
  }

  /** PUT a form.  */
  async function handleUpdateForm() {
    const params: PutFacilityFormParams = {
      id: Number(id),
      title: editFormModal.formTitle,
    };

    const res = await PutFacilityForm(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving form.")));
      return;
    }

    dispatch(showSuccess(t("Form successfully saved.")));
    setFormTitle(editFormModal.formTitle);
    closeEditFormModal();
  }

  async function handleUpdateProcessForm() {
    const params: PutFacilityFormParams = {
      id: Number(id),
      process_product_with_response: formProcess.updatedCheck,
    };

    const res = await PutFacilityForm(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating form")));
      return;
    }

    setFormProcess(prevState => ({
      ...prevState,
      updatedCheck: !!res.data?.process_product_with_response,
      previousCheck: !!res.data?.process_product_with_response,
    }));
    dispatch(showSuccess(t("Form successfully updated")));
  }

  function closeNewInputModal() {
    setNewInputModal({
      form_id: Number(id),
      open: false,
      type: "none",
      label: "",
      help_text: "",
    });
  }

  function closeFormInputEditModal() {
    setSelectedFormInput(prev => ({ ...prev, id: null, editModalOpen: false }));
  }

  async function updateFormInputRowPositions() {
    const updatedFormInputRows: FormInputRows = generateInputRowsWithSequentialPositions(
      formInputRowsContainer.updatedState,
    );

    const formInputPositions: PutFormInputPosition[] = [];

    Object.keys(updatedFormInputRows).forEach(row => {
      updatedFormInputRows[row].forEach(formInput => {
        formInputPositions.push({ id: formInput.id, row: formInput.row, column: formInput.column });
      });
    });

    const putFormInputPositions = await PutFormInputPositions({ inputs: formInputPositions });

    if (putFormInputPositions.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating form input positions")));
      return undefined;
    }

    formInputRowsContainerActions.set(updatedFormInputRows);
    return updatedFormInputRows;
  }

  function cancelChanges() {
    if (unsavedChangesExist(formProcess.updatedCheck, formProcess.previousCheck)) {
      setFormProcess(prevState => ({ ...prevState, updatedCheck: prevState.previousCheck }));
    }
    if (formInputRowsContainer?.changesExist) {
      formInputRowsContainerActions.rollback();
    }
  }

  function handleCheckboxChange() {
    setFormProcess(prevState => ({ ...prevState, updatedCheck: !prevState.updatedCheck }));
  }

  function handleUpdate() {
    if (formInputRowsContainer?.changesExist) {
      void updateFormInputRowPositions();
    }

    if (unsavedChangesExist(formProcess.previousCheck, formProcess.updatedCheck)) {
      void handleUpdateProcessForm();
    }
  }

  function allAttributesUsed() {
    let attributes = ["users_first_name", "users_last_name", "users_email", "users_phone"];
    let allAttributesUsed = false;
    Object.keys(formInputRowsContainer?.updatedState)?.forEach(formKey => {
      formInputRowsContainer?.updatedState[formKey]?.forEach(input => {
        const foundIndex = attributes?.findIndex(attr => attr === input.assigned_attribute);
        if (foundIndex !== -1) {
          attributes = attributes.filter((__, index) => index !== foundIndex);
        }
      });
    });
    if (attributes?.length === 0) {
      allAttributesUsed = true;
    }
    return allAttributesUsed;
  }

  return (
    <Page
      title={`${formTitle ?? ""} `}
      notificationBarProps={{
        isVisible:
          formInputRowsContainer?.changesExist ||
          unsavedChangesExist(formProcess.updatedCheck, formProcess.previousCheck),
        onAction: handleUpdate,
        onCancel: cancelChanges,
      }}
      secondaryActions={[
        {
          action: openEditFormModal,
          content: t("Edit title"),
        },
      ]}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Forms"),
          url: "/admin/settings/forms",
        },
      ]}
    >
      <hr />
      {!hideAddInput ? (
        <div className="form-add-input-container">
          <Button type="text" onClick={() => setNewInputModal(prev => ({ ...prev, open: true }))}>
            <FontAwesomeIcon className="form-add-input-icon" size="sm" icon={["far", "plus"]} />
            <span>{t("Add Input")}</span>
          </Button>
        </div>
      ) : null}

      <Card>
        {formInputRowsContainer.updatedState && formProcess.updatedCheck && !allAttributesUsed() ? (
          <Callout
            title={t("Attributes Have Not Been Assigned to Inputs")}
            content={t("All attributes must be assigned to an input in order to process products using this form")}
            type="warning"
          />
        ) : null}
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div className="pt-3">
                <Checkbox
                  id="process_form"
                  size="small"
                  checked={formProcess?.updatedCheck}
                  // value={formProcessCheckbox === true ? true : false}
                  onChange={handleCheckboxChange}
                  label={t("Process Product with Responses")}
                  supportText={
                    t("Use responses submitted by the customer to process products such as memberships, add ons, tickets, etc.")
                  }
                />
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <DndProvider backend={HTML5Backend}>
        <FormEditContext.Provider
          value={{
            selectedFormInput,
            setSelectedFormInput,
            formInputRowsContainer,
            formInputRowsContainerActions,
            handleUpdateInput,
            closeFormInputEditModal,
          }}
        >
          {!formInputRowsContainer?.updatedState ? (
            <div style={{ height: "106px", overflow: "hidden", transform: "scale(0.5)" }}>
              <Spin />
            </div>
          ) : (
            <Card>
              <Card.Section>
                <FormEditInputLayout setNewInputModal={setNewInputModal} />
              </Card.Section>
            </Card>
          )}
        </FormEditContext.Provider>
      </DndProvider>

      {/* Edit Form Modal */}
      <Portal isMounted={editFormModal.open}>
        <Sheet
          open={editFormModal.open}
          onOk={handleUpdateForm}
          okText={t("Update")}
          okDisabled={editFormModal.formTitle === ""}
          onCancel={closeEditFormModal}
          size="small"
          title={t("Update Form Title")}
        >
          <Input
            label={t("Form Title")}
            value={editFormModal.formTitle}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEditFormModal(prev => ({ ...prev, formTitle: e.target.value }))
            }
          />
        </Sheet>
      </Portal>

      {/* New Input Modal */}
      <Portal isMounted={newInputModal.open}>
        <Sheet
          open={newInputModal.open}
          onOk={() => handleAddNewInput()}
          okText={t("Create")}
          onCancel={() => closeNewInputModal()}
          okDisabled={newInputModal.type === "none" || !newInputModal.label}
          size="small"
          title={t("New Input")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("Input Type")}
                onChange={(value: FormInputTypes | "none") => setNewInputModal(prev => ({ ...prev, type: value }))}
                defaultValue={"none"}
              >
                {(["none", "input", "select", "checkbox", "text"] as FormInputTypes[]).map((value, index) => {
                  return (
                    <Option key={index} value={value} name={capitalize(value)}>

                      {value === "input" ? t("Textbox") : capitalize(value)}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Label")}
                value={newInputModal.label}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setNewInputModal(prev => ({ ...prev, label: e.target.value }))
                }
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={
                  newInputModal?.type === "text"
                    ? t("Subtext")
                    : t("Help Text")
                }
                value={newInputModal.help_text}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setNewInputModal(prev => ({ ...prev, help_text: e.target.value }))
                }
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      {/* Remove Input Popup */}
      <Portal isMounted={selectedFormInput.deletePopupOpen}>
        <Popup
          open={selectedFormInput.deletePopupOpen}
          type="warning"
          title={t("Delete Input?")}
          description={t("Are you sure you want to remove the input from this form?")}
          onCancel={() => setSelectedFormInput(prev => ({ ...prev, id: null, deletePopupOpen: false }))}
          onOk={() => handleRemoveFormInput(selectedFormInput.id)}
        />
      </Portal>
    </Page>
  );
}
